/**
 * Per-student "Insights & Share" panel used from the counsellor
 * dashboard. Handles two things:
 *
 *   1. Generate + regenerate the LLM analysis (candid counsellor
 *      version + warmer student-facing version). First regen free,
 *      further regens gated with an upgrade nudge.
 *
 *   2. Enable a shareable link (90-day TTL) that sends the student to
 *      /s/{token} with the warm analysis + shortlist. Copy-to-clipboard
 *      + view counter + revoke.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Sparkles, Share2, Copy, Check, RefreshCw, XCircle, ShoppingCart, Mail } from "lucide-react";
import { toast } from "sonner";


export function InsightsShareDialog({ cohortId, student, trigger, onChanged }) {
  const [open, setOpen] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pollingId, setPollingId] = useState(null);

  // Absolute URL — the public share page expects the origin from the
  // browser's window, not the backend's FRONTEND_ORIGIN env.
  const shareUrl = share?.share_token
    ? `${window.location.origin}/s/${share.share_token}`
    : share?.share_url;

  const loadAnalysis = async () => {
    try {
      const r = await api.get(`/counsellors/cohorts/${cohortId}/students/${student.id}/analysis`);
      setAnalysis(r.data);
      return r.data;
    } catch { return null; }
  };

  useEffect(() => {
    if (!open) return;
    loadAnalysis();
    // Pre-fill share state from the student row itself (already
    // returned by /cohorts/{id}).
    if (student.share_token && student.share_enabled) {
      setShare({
        share_enabled: true,
        share_token: student.share_token,
        share_expires_at: student.share_expires_at,
        share_view_count: student.share_view_count || 0,
        share_last_viewed_at: student.share_last_viewed_at,
      });
    } else {
      setShare(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll while status=generating.
  useEffect(() => {
    if (!open) return;
    if (analysis?.status !== "generating") {
      if (pollingId) { clearInterval(pollingId); setPollingId(null); }
      return;
    }
    if (pollingId) return;
    const id = setInterval(async () => {
      const r = await loadAnalysis();
      if (r?.status !== "generating") {
        clearInterval(id);
        setPollingId(null);
      }
    }, 3000);
    setPollingId(id);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, analysis?.status]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/counsellors/cohorts/${cohortId}/students/${student.id}/analysis`);
      toast.info("Generating analysis — takes 15-30 seconds");
      setTimeout(() => loadAnalysis(), 500);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.error === "regen_limit") {
        toast.error(d.message);
      } else {
        toast.error(typeof d === "string" ? d : "Could not generate analysis");
      }
    } finally {
      setBusy(false);
    }
  };

  const enableShare = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/counsellors/cohorts/${cohortId}/students/${student.id}/share`);
      setShare(r.data);
      toast.success("Share link enabled");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not enable share");
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    if (!confirm("Rotate the link? The old URL will stop working immediately.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/counsellors/cohorts/${cohortId}/students/${student.id}/share/rotate`);
      setShare(r.data);
      toast.success("New link generated");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!confirm("Revoke the share link? The student won't be able to open it anymore.")) return;
    setBusy(true);
    try {
      await api.post(`/counsellors/cohorts/${cohortId}/students/${student.id}/share/revoke`);
      setShare(null);
      toast.success("Share revoked");
      onChanged?.();
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — user can still select the link */ }
  };

  const emailToStudent = async () => {
    if (!student?.email) {
      toast.error("Add an email on this student's roster row first");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/counsellors/cohorts/${cohortId}/students/${student.id}/share/email`,
        {}, { headers: { "X-Requested-With": "XMLHttpRequest" } },
      );
      toast.success(`Share link emailed to ${student.email}`);
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      const code = d?.code;
      if (code === "missing_email") {
        toast.error("Add an email on this student's roster row first");
      } else if (code === "share_not_enabled") {
        toast.error("Enable the share link first (button above)");
      } else if (code === "email_sender_not_configured") {
        toast.error("Email sender not configured on this environment");
      } else if (code === "delivery_failed") {
        toast.error("Email provider rejected the address");
      } else {
        toast.error(d?.message || "Could not send email");
      }
    } finally {
      setBusy(false);
    }
  };

  const canRegen = analysis && (analysis.regen_count || 0) < (analysis.free_regens ?? 1);
  const credits = analysis?.credits_remaining ?? 0;

  const openTopUp = async () => {
    setBusy(true);
    try {
      const r = await api.post("/counsellors/topup/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Insights &amp; share · {student.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1" data-testid="insights-panel">
          {/* SHARE */}
          <section className="border border-stone-200 rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Share2 className="w-4 h-4 text-stone-600" />
                <div className="label-uppercase !text-[10px] text-stone-500">Shareable link</div>
              </div>
              {share?.share_expires_at && (
                <span className="text-[10px] font-mono text-stone-500">
                  expires {new Date(share.share_expires_at).toISOString().slice(0, 10)}
                </span>
              )}
            </div>
            {share ? (
              <div className="mt-3 space-y-3">
                <div className="flex items-stretch gap-2">
                  <div
                    className="flex-1 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-xs font-mono truncate select-all"
                    data-testid="share-link-value"
                    title={shareUrl}
                  >
                    {shareUrl}
                  </div>
                  <Button size="sm" variant="outline" onClick={copyLink} data-testid="share-copy-btn">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    <span className="ml-1">{copied ? "Copied" : "Copy"}</span>
                  </Button>
                </div>
                <div className="text-xs text-stone-500 flex items-center gap-4 flex-wrap">
                  <span data-testid="share-view-count">
                    {share.share_view_count || 0} view{share.share_view_count === 1 ? "" : "s"}
                  </span>
                  {share.share_last_viewed_at && (
                    <span>last opened {new Date(share.share_last_viewed_at).toISOString().slice(0, 16).replace("T", " ")} UTC</span>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={emailToStudent}
                    disabled={busy || !student?.email}
                    data-testid="share-email"
                    className="bg-stone-900 hover:bg-stone-800 text-white"
                    title={student?.email
                      ? `Send this link to ${student.email}`
                      : "Add an email on this student's roster row first"}
                  >
                    <Mail className="w-3.5 h-3.5 mr-1" /> Email link to student
                  </Button>
                  <Button size="sm" variant="outline" onClick={rotate} disabled={busy} data-testid="share-rotate">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rotate link
                  </Button>
                  <Button size="sm" variant="ghost" onClick={revoke} disabled={busy} data-testid="share-revoke" className="text-rose-600">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Revoke
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-3">
                <p className="text-sm text-stone-600 leading-relaxed">
                  Enable a private link (90-day expiry) you can send the student. They'll see the six
                  matched courses plus a warm, personalised note.
                </p>
                <Button
                  size="sm"
                  onClick={enableShare}
                  disabled={busy}
                  data-testid="share-enable"
                  className="mt-3 bg-stone-900 hover:bg-stone-800 text-white"
                >
                  <Share2 className="w-3.5 h-3.5 mr-1" /> Enable share link
                </Button>
              </div>
            )}
          </section>

          {/* ANALYSIS */}
          <section className="border border-stone-200 rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-stone-600" />
                <div className="label-uppercase !text-[10px] text-stone-500">WUA analysis</div>
              </div>
              {analysis?.status === "generating" && (
                <span className="text-[10px] font-mono text-amber-700">generating…</span>
              )}
            </div>

            {(!analysis || analysis.status === "none") && (
              <div className="mt-3">
                <p className="text-sm text-stone-600 leading-relaxed">
                  A 2-3 paragraph editorial write-up per student: fit summary, top-pick reasoning,
                  and what to watch. Powered by Claude via WUA's LLM helper. Takes ~20 seconds.
                </p>
                <Button
                  size="sm"
                  onClick={generate}
                  disabled={busy}
                  data-testid="analysis-generate"
                  className="mt-3 bg-stone-900 hover:bg-stone-800 text-white"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> Generate analysis
                </Button>
              </div>
            )}

            {analysis?.status === "generating" && (
              <div className="mt-3 text-sm text-stone-500 italic" data-testid="analysis-generating">
                Working through the shortlist and profile — this refreshes automatically.
              </div>
            )}

            {analysis?.status === "ready" && analysis.counsellor && (
              <div className="mt-4 space-y-4" data-testid="analysis-ready">
                <div>
                  <div className="label-uppercase !text-[10px] text-stone-500 mb-2">
                    Your (counsellor) analysis — candid
                  </div>
                  <div
                    className="text-sm text-stone-800 leading-relaxed whitespace-pre-line"
                    data-testid="analysis-counsellor-text"
                  >
                    {analysis.counsellor}
                  </div>
                </div>
                {shareUrl && (
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-stone-600 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 inline-block"
                    data-testid="preview-student-view"
                  >
                    Preview what the student sees on their share link →
                  </a>
                )}
                <div className="pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-stone-500">
                    {analysis.generated_at && `generated ${new Date(analysis.generated_at).toISOString().slice(0, 16).replace("T", " ")} UTC`}
                  </span>
                  {canRegen ? (
                    <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid="analysis-regen">
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate (free)
                    </Button>
                  ) : credits > 0 ? (
                    <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid="analysis-regen-credit">
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate ({credits} credit{credits === 1 ? "" : "s"} left)
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={openTopUp} disabled={busy} data-testid="analysis-topup">
                      <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Buy 10 regens · £5
                    </Button>
                  )}
                </div>
              </div>
            )}

            {analysis?.status === "error" && (
              <div className="mt-3 text-sm text-rose-700" data-testid="analysis-error">
                Analysis failed. Try again in a minute. No credit was used.
              </div>
            )}

            {analysis?.status === "llm_unavailable" && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="analysis-llm-unavailable">
                <div className="font-medium">LLM is temporarily unavailable</div>
                <div className="mt-1 text-amber-800/85 leading-relaxed">
                  The provider hit its daily spend cap. Try Regenerate in a few
                  minutes — <strong>no credit was used</strong> for this attempt.
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid="analysis-retry">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry
                  </Button>
                  {analysis.counsellor && (
                    <span className="text-[11px] text-amber-800/70">Previous analysis is still shown below.</span>
                  )}
                </div>
              </div>
            )}

            {analysis?.status === "llm_unavailable" && analysis.counsellor && (
              <div className="mt-4 space-y-4" data-testid="analysis-stale-visible">
                <div>
                  <div className="label-uppercase !text-[10px] text-stone-500 mb-2">
                    Your (counsellor) analysis — candid
                  </div>
                  <div
                    className="text-sm text-stone-800 leading-relaxed whitespace-pre-line"
                    data-testid="analysis-counsellor-text"
                  >
                    {analysis.counsellor}
                  </div>
                </div>
                <div className="text-xs text-stone-500">
                  {analysis.generated_at && `generated ${new Date(analysis.generated_at).toISOString().slice(0, 16).replace("T", " ")} UTC`}
                </div>
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
